/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    formatProductAnalyticsReportDates,
    getEnabledRollUps,
    formatProductAnalyticsTableDate,
    getChartColorsSubset,
    chartZAxisColor
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './totalanswersreport_styles';


/* ========== ~~~~~~~~~~ TRIVIA AND POLLS ANALYTICS - TOTAL ANSWERS REPORT ~~~~~~~~~~ ========== */
const emptyObj = {};
const chartTableColors = getChartColorsSubset(2)
const products = [
    { id: 'polls', name: 'Polls', avgName: 'avgPollCardsPerUniqueUser', color: chartTableColors[0] },
    { id: 'quizzes', name: 'Quizzes', avgName: 'avgQuizCardsPerUniqueUser', color: chartTableColors[1] },
    { id: 'totalAnswers', name: 'Total', color: '#fff' },
    { id: 'avgCardsPerUniqueUser', name: 'Avg. Cards per Unique User', color: chartZAxisColor },
];
const TotalAnswersReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    const { overview = emptyObj, details, status } = useSelector(state => state.triviaAndPolls.totalAnswers, shallowEqual);
    const filters = useSelector(state => state.app.filters);
    const { compareTo: changeType } = filters;

    const totalIndex = products.findIndex(product => product.id === 'totalAnswers');
    const avgIndex = products.map(product => product.id).indexOf('avgCardsPerUniqueUser');

    // component states
    const [chartLegends, setChartLegends] = useState(products.map(product => ({ ...product, value: 0, changeRate: 0, checked: true })));
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    const computeTableTotal = (products, checkedOnly = true) => {
        // Compute total depending on the selected products
        const total = products.filter(product => product.id !== 'totalAnswers' && product.id !== 'avgCardsPerUniqueUser' && (checkedOnly ? (product.checked === true) : true))
            .map(product => product.value)
            .reduce((prev, current) => prev + current, 0);
        return total;
    }

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: overview?.period?.[product.id] || 0,
            changeRate: overview?.period?.[`${product.id}RateChange`] || 0,
            checked: chartLegends.length ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        productArr[totalIndex] = {
            ...productArr[totalIndex],
            value: computeTableTotal(productArr)
        }

        // show the average based on the selected product
        if(chartLegends.length > 0) {
            productArr[avgIndex] = {
                ...productArr[avgIndex],
                ...computeAvg({legends: productArr})
            }
        }

        setChartLegends(productArr);
    }

    const barChartKeys = useMemo(() => products.map(product => product.name).reverse(), []);

    const barChartColors = useMemo(() => products.map(product => product.color).reverse(), [])

    const computeAvg = ({legends, selectedDate, showTotalAvg = true}) => {
        const showAggregate = showTotalAvg && showTotal && !selectedDate;
        const overviewDetail = overview?.period;
        const selectedDetail = details[selectedInterval][selectedDate];
        const updatedLegends = [...legends];

        // if all products are selected, show the overview or details of average cards per unique user
        if(updatedLegends.filter(legend => legend.checked).length === updatedLegends.length) {
            updatedLegends[avgIndex] = {
                ...updatedLegends[avgIndex],
                value: showAggregate ? overviewDetail?.avgCardsPerUniqueUser : selectedDetail?.avgCardsPerUniqueUser,
                changeRate: showAggregate ? overviewDetail?.avgCardsPerUniqueUserRateChange : selectedDetail?.avgCardsPerUniqueUserRateChange
            }
        } 
        // else if only one is selected, show the overview or details of either the average polls or quizzes per unique user
        else {
            // if one of the product is checked
            const checkedAnswerType = updatedLegends.filter(legend => legend.checked && legend.avgName).map(legend => legend.avgName);
            if(checkedAnswerType.length === 1) {
                updatedLegends[avgIndex] = {
                    ...legends[avgIndex],
                    value: showAggregate ? overviewDetail?.[checkedAnswerType] : selectedDetail?.[checkedAnswerType],
                    changeRate: showAggregate ? overviewDetail?.[`${checkedAnswerType}RateChange`] : selectedDetail?.[`${checkedAnswerType}RateChange`]
                }
            } 
        }

        return updatedLegends[avgIndex];
    }
    
    const barChartLegends = useMemo(() => {
        const totalValue = computeTableTotal(chartLegends, false);

        return chartLegends.map((legend, index) => {
            const productPercentage = totalValue > 0 ? ((legend.value ? legend.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';

            return (
                <ChartLegend
                    title={`${legend.name} ${chartLegends.length > 2 && legend.id !== 'totalAnswers' && legend.id !== 'avgCardsPerUniqueUser' ? productPercentageText : ''}`}
                    checkboxColor={legend.color}
                    checked={legend.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }
                        legends[totalIndex] = {
                            ...legends[totalIndex],
                            value: computeTableTotal(legends)
                        }

                        legends[avgIndex] = {
                            ...legends[avgIndex], 
                            ...computeAvg({legends, selectedDate})
                        }

                        setChartLegends(legends);
                    }}
                    value={formatNumber(legend.value, 'number')}
                    changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(legend.changeRate || 0, 'percent')}
                    changeType={changeType}
                    metricType={'trivia'}
                />
            )
        });
    }, [changeType, chartLegends, totalIndex])

    const transformedBarChartData = useMemo(() => {
        const dates = details ? details[selectedInterval] : {};

        const barChartData = Object.keys(dates).map(date => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = { date: formattedDate }
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if (checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    const checkedAnswerType = chartLegends.filter(legend => legend.checked && legend.avgName).map(legend => legend.avgName);
                    const showAggregateAvg = chartLegends.filter(legend => legend.checked).length === chartLegends.length;
                    const selectedAnswerType = showAggregateAvg ? 'avgCardsPerUniqueUser' : (checkedAnswerType.length === 1 ? checkedAnswerType : 'avgCardsPerUniqueUser')
                    if (product.id === 'avgCardsPerUniqueUser')
                        item['z'] = detailsDateData[selectedAnswerType]
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }, [chartLegends, details, selectedInterval])

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] || null;
            if (productDetails) {
                value = productDetails[product.id] || 0;
                changeRate = productDetails[`${product.id}RateChange`] || 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        legends[totalIndex] = {
            ...legends[totalIndex],
            value: computeTableTotal(legends)
        }

        legends[avgIndex] = {
            ...legends[avgIndex], 
            ...computeAvg({legends, selectedDate: date, showTotalAvg: false})
        }

        setSelectedDate(date);
        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedDate(null);
        setSelectedPeriodX('');
    }


    const cardtoggles = (
        <CardToggles>
            <CardToggle
                label={'Total Answers'}
                compareType={changeType}
                metricType={'trivia'}
                percentChange={{
                    value: overview?.period ? formatNumber(overview.period.totalAnswersRateChange, 'percent') : 0,
                    direction: overview?.period ? getChangeRateDirection(overview.period.totalAnswersRateChange) : 'up'
                }}
                selected={true}
                value={overview?.period ? formatNumber(overview.period.totalAnswers) : ''}
            />
        </CardToggles>
    );

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={barChartColors}
            keys={barChartKeys}
            data={transformedBarChartData}
            period={selectedInterval}
            loading={status === 'pending' ? true : false}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            zAxisId={'avgCardsPerUniqueUser'}
            zAxisColor={chartZAxisColor}
        />
    );

    const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'trivia'}
            products={products}
            data={details ? details[selectedInterval] : null}
            dataType={'trivia'}
            csvFileName={'Total Answers Report'}
        />
    ) : []

    const isNoData = status === 'rejected' ||
        (status === 'fulfilled' && !overview?.period?.totalAnswers) || 
        (status === 'fulfilled' && !Object.keys(overview).length);


    // show aggregate total on load of page
    useEffect(() => {
        if (status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if (showTotal) showAggregateTotal();
    }, [showTotal]);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if (status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'trivia');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);


    return (
        <div name={'totalanswersreport'} klass={`${props.classes.totalanswersreport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Total Answers'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>Total Answers</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                                Break down of answers between Polls and Quizzes. A “Unique User” for Trivia & Polls is defined by a user who answered at least one card during the rolled-up time period (daily, weekly, monthly, etc). The Avg Cards per Unique User is calculated by taking the Total Answers and dividing it by total unique users (as defined above)
                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                noData={isNoData}
                enabledPeriods={enabledRollups}
                chartLegend={barChartLegends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default TotalAnswersReport;